import { DictType } from '@pma/types';
import { toast } from '@hooks/use-toast';
import {
  PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import { cn } from '@pma/utils/helpers';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { parseCookies } from 'nookies';
import { ReactNode, useState } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import useTranslator from '@hooks/use-translator';
import AddWorkTypeForm from './add-work-type-form';

interface Props {
  id: number;

  children: ReactNode;
  value: DictType;
  refetch?: CallableFunction;
}
const ProjectWorkTypeDictionaryEditor = ({
  children,
  id,

  value,
  refetch,
}: Props) => {
  const [editMode, setEditMode] = useState(false);
  const [hover, setHover] = useState(false);

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { translate, locale } = useTranslator();

  const isHebrew = locale === 'he';

  const deleteWorkType = async () => {
    const response = await axios.delete(
      SERVER_URL + PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT + `/delete/${id}`,
      {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response?.data;
  };

  const deleteWorkTypeMutation = useMutation({
    mutationKey: ['delete-work-type', id],
    mutationFn: deleteWorkType,
    onSuccess: () => {
      refetch && refetch();
      toast({
        title: translate('Success'),
        description: translate('Workspace work types updated'),
        variant: 'success',
      });
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate('Error'),
        description: errorMessage || translate('Unable to delete work type'),
        variant: 'error',
      });
    },
  });

  return (
    <div
      className={cn(
        'flex items-center justify-start gap-2 text-center text-black h-fit font-semibold w-full my-0.5 cursor-pointer hover:border-primary hover:border-3',
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!editMode && (
        <div className='relative h-full w-full text-left'>
          {hover && (
            <>
              <Edit2
                className={cn(
                  'absolute z-40 top-0 text-primary hover:border-3',
                  isHebrew ? '-left-0 rotate-[270deg]' : '-right-0',
                )}
                onClick={() => setEditMode(true)}
                size={14}
              />
              <Trash2
                className={cn(
                  'absolute z-40 top-4 text-destructive hover:border-3',
                  isHebrew ? '-left-0' : '-right-0',
                )}
                onClick={() => deleteWorkTypeMutation.mutate()}
                size={14}
              />
            </>
          )}
          {children}
        </div>
      )}
      {editMode && (
        <AddWorkTypeForm
          option={{
            id,
            value,
            onCancel: () => setEditMode(false),
          }}
          refetch={refetch}
        />
      )}
    </div>
  );
};

export default ProjectWorkTypeDictionaryEditor;
